/**
 * Enum representing colors for TKD belt levels
 */
export enum CinturonColor {
  LOADING = '#e0e0e0',
  DEFAULT = '#FFFFFF',
  BLANCO = '#FFFFFF',
  AMARILLO = '#FAE000',
  VERDE = '#2A8D2E',
  AZUL = '#085796',
  ROJO = '#D10E00',
  NEGRO = '#000000'
}

/**
 * Interface for representing split belt colors
 */
export interface BeltColorInfo {
  isPunta: boolean;
  mainColor: string;
  secondaryColor?: string;
}

/**
 * Utility class for mapping belt (cinturón) descriptions to colors
 */
export class CinturonColorUtil {
  /**
   * Maps graduation ID directly to a color or color combination
   * Based on the database schema:
   * 1=BLANCO, 2=P.AMARILLA, 3=AMARILLO, 4=P.VERDE, 5=VERDE, 
   * 6=P.AZUL, 7=AZUL, 8=P.ROJA, 9=ROJO, 10=P.NEGRA, 
   * 11=I DAN, 12=II DAN, 13=III DAN, etc.
   */
  static getColorInfoById(id: number | null | undefined): BeltColorInfo {
    if (!id) return { isPunta: false, mainColor: CinturonColor.LOADING };
    
    // Map IDs to colors based on the CSV database values
    switch (id) {
      case 1:  return { isPunta: false, mainColor: CinturonColor.BLANCO }; // BLANCO
      case 2:  return { isPunta: true, mainColor: CinturonColor.BLANCO, secondaryColor: CinturonColor.AMARILLO }; // PUNTA AMARILLA
      case 3:  return { isPunta: false, mainColor: CinturonColor.AMARILLO }; // AMARILLO
      case 4:  return { isPunta: true, mainColor: CinturonColor.AMARILLO, secondaryColor: CinturonColor.VERDE }; // PUNTA VERDE
      case 5:  return { isPunta: false, mainColor: CinturonColor.VERDE }; // VERDE
      case 6:  return { isPunta: true, mainColor: CinturonColor.VERDE, secondaryColor: CinturonColor.AZUL }; // PUNTA AZUL
      case 7:  return { isPunta: false, mainColor: CinturonColor.AZUL }; // AZUL
      case 8:  return { isPunta: true, mainColor: CinturonColor.AZUL, secondaryColor: CinturonColor.ROJO }; // PUNTA ROJA
      case 9:  return { isPunta: false, mainColor: CinturonColor.ROJO }; // ROJO
      case 10: return { isPunta: true, mainColor: CinturonColor.ROJO, secondaryColor: CinturonColor.NEGRO }; // PUNTA NEGRA
      case 11: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // I DAN
      case 12: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // II DAN
      case 13: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // III DAN
      case 14: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // IV DAN
      case 15: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // V DAN
      case 16: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // VI DAN
      case 17: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // VII DAN
      case 18: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // VIII DAN
      case 19: return { isPunta: false, mainColor: CinturonColor.NEGRO }; // IX DAN
      default: return { isPunta: false, mainColor: CinturonColor.DEFAULT };
    }
  }

  /**
   * For backward compatibility - gets just the main color
   */
  static getColorById(id: number | null | undefined): string {
    return this.getColorInfoById(id).mainColor;
  }

  /**
   * Maps a belt description to its corresponding color information
   * @param nombre Belt description from database
   */
  static getColorInfoFromNombre(nombre: string | undefined | null): BeltColorInfo {
    if (!nombre) return { isPunta: false, mainColor: CinturonColor.DEFAULT };
    
    const uppercaseDesc = nombre.toUpperCase();
    
    // Check for "PUNTA" belts first
    if (uppercaseDesc.includes('PUNTA')) {
      if (uppercaseDesc.includes('AMARILLA')) {
        return { isPunta: true, mainColor: CinturonColor.BLANCO, secondaryColor: CinturonColor.AMARILLO };
      } else if (uppercaseDesc.includes('VERDE')) {
        return { isPunta: true, mainColor: CinturonColor.AMARILLO, secondaryColor: CinturonColor.VERDE };
      } else if (uppercaseDesc.includes('AZUL')) {
        return { isPunta: true, mainColor: CinturonColor.VERDE, secondaryColor: CinturonColor.AZUL };
      } else if (uppercaseDesc.includes('ROJA')) {
        return { isPunta: true, mainColor: CinturonColor.AZUL, secondaryColor: CinturonColor.ROJO };
      } else if (uppercaseDesc.includes('NEGRA')) {
        return { isPunta: true, mainColor: CinturonColor.ROJO, secondaryColor: CinturonColor.NEGRO };
      }
    }
    
    // Full color belts
    if (uppercaseDesc.includes('AMARILLO')) {
      return { isPunta: false, mainColor: CinturonColor.AMARILLO };
    } else if (uppercaseDesc.includes('VERDE')) {
      return { isPunta: false, mainColor: CinturonColor.VERDE };
    } else if (uppercaseDesc.includes('AZUL')) {
      return { isPunta: false, mainColor: CinturonColor.AZUL };
    } else if (uppercaseDesc.includes('ROJO')) {
      return { isPunta: false, mainColor: CinturonColor.ROJO };
    } else if (uppercaseDesc.includes('NEGRO') || uppercaseDesc.includes('DAN')) {
      return { isPunta: false, mainColor: CinturonColor.NEGRO };
    } else if (uppercaseDesc.includes('BLANCO')) {
      return { isPunta: false, mainColor: CinturonColor.BLANCO };
    }
    
    return { isPunta: false, mainColor: CinturonColor.BLANCO };
  }
  
  /**
   * For backward compatibility - gets just the main color
   */
  static getColorFromNombre(nombre: string | undefined | null): string {
    return this.getColorInfoFromNombre(nombre).mainColor;
  }
}